import { CircularProgress } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { mapValues } from 'lodash-es';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { style } from 'typestyle';
import { useRecoilEffect } from '../../../hooks/useRecoilEffect';
import { cardPropsAtom, cardVisibilityAtom, muiThemeAtom } from '../atoms';
import {
  CardProps,
  defaultState,
  SetCardVisibility
} from '../Cards/defaultState';
import { Main } from './Main';

export interface RootComponentProps {
  /*
   * Handle message from iframe
   */
  onMessage?: (event: any) => void;
  /*
   * Handle screenshot image change
   */
  onThumbnailChange?: (value: string) => void;
}

const cn = {
  root: style({
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  })
};

export default function RootComponent({
  onMessage,
  onThumbnailChange
}: RootComponentProps) {
  const initialized = useSelector(state => state.file.initialized);
  let files = useSelector(state => state.file.files);
  files = files.filter(file => !file.isTrashed);

  const muiTheme = useRecoilValue(muiThemeAtom);

  const setCardVisibility: SetCardVisibility = useRecoilCallback(
    ({ set }, name: string, visible = false) => {
      set(cardVisibilityAtom, curr => ({
        ...curr,
        [name]: visible
      }));
    },
    []
  );

  const cardProps = useRecoilValue(cardPropsAtom);
  useRecoilEffect(
    ({ set }) => {
      if (!initialized && cardProps) {
        // カードの設定を初期化する
        set(cardPropsAtom, null);
      }
      if (initialized && !cardProps) {
        // feeles/card.json を探してカードの設定を初期化する
        let cardProps: CardProps = defaultState; // デフォルト値を使う. 以前はファイルが存在しない場合は例外で終了していた
        const card = files.find(file => file.name === 'feeles/card.json');
        if (card) {
          try {
            cardProps = JSON.parse(card.text);
          } catch (error) {
            console.error(`Invalid feeles/card.json: ${card.text}`);
          }
        } else {
          console.error(
            'Failed to load feeles/card.json. Launch with default config.'
          );
        }
        set(cardPropsAtom, cardProps);
        set(cardVisibilityAtom, mapValues(cardProps, 'visible'));
      }
    },
    [initialized, cardProps]
  );

  return (
    <MuiThemeProvider theme={muiTheme}>
      {!initialized || !cardProps ? (
        <div className={cn.root}>
          <CircularProgress size={60} color="primary" />
        </div>
      ) : (
        <Main
          setCardVisibility={setCardVisibility}
          onMessage={onMessage}
          onThumbnailChange={onThumbnailChange}
        />
      )}
    </MuiThemeProvider>
  );
}
